import type { EvidenceSource } from "./types";

export interface ParsedParticipant {
  participantId: string;
  name: string;
  department: string;
  role: string;
  timestamp: string;
  vectorScores: (number | null)[]; // index 0..4 => vectors 1..5
  source: EvidenceSource;
  sourceLabel: string;
  notes: string | null;
}

export type SchemaKind =
  | "ai-readiness"
  | "forms"
  | "lucid"
  | "poll"
  | "quotes"
  | "unknown";

// Pick the most likely delimiter by comparing tab vs comma counts on the header
// line. Quote/analysis tables pasted out of Excel/Word arrive tab-separated;
// true exports are comma-separated. Defaults to comma.
export function sniffDelimiter(text: string): string {
  const firstLine = text.replace(/\r\n/g, "\n").split("\n")[0] ?? "";
  const tabs = (firstLine.match(/\t/g) || []).length;
  const commas = (firstLine.match(/,/g) || []).length;
  return tabs > commas ? "\t" : ",";
}

// Minimal robust CSV parser: handles quoted fields, the given delimiter, and
// escaped quotes. Delimiter defaults to comma; pass "\t" for TSV.
export function parseCSV(text: string, delimiter = ","): string[][] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;
  const src = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === delimiter) {
      row.push(field);
      field = "";
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += c;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}

export function detectSchema(headers: string[]): SchemaKind {
  const h = headers.map((x) => x.trim().toLowerCase());
  const has = (needle: string) => h.some((x) => x.includes(needle));

  // Graded-quote tables (Copilot / Lucidchart exports): one quote per row, tied
  // to a Dimension (1..5) with a Score. Detected first because "Quote..." starts
  // with "q" and would otherwise be mistaken for a Forms export.
  if (has("quote") && (has("dimension") || has("speaker"))) return "quotes";

  const hasVectorScores = ["v1_score", "v2_score", "v3_score", "v4_score", "v5_score"].every(
    (k) => h.includes(k)
  );
  if (hasVectorScores) return "ai-readiness";
  if (h.some((x) => x.includes("respondent") || x.startsWith("q") || x.includes("response"))) {
    return "forms";
  }
  // Lucidchart CSV exports carry shape/page columns (e.g. "Shape Library",
  // "Text Area 1", "Page ID"); also match explicit board/lucid hints.
  if (
    h.some(
      (x) =>
        x.includes("lucid") ||
        x.includes("shape") ||
        x.includes("text area") ||
        x.includes("board") ||
        x.includes("sticky")
    )
  ) {
    return "lucid";
  }
  if (h.includes("score") || h.includes("rating")) return "poll";
  return "unknown";
}

const SOURCE_MAP: Record<string, { source: EvidenceSource; label: string }> = {
  poll: { source: "poll", label: "Poll Response" },
  lucid: { source: "lucid", label: "Lucidchart Activity" },
  lucidchart: { source: "lucid", label: "Lucidchart Activity" },
  copilotnotes: { source: "copilot", label: "Copilot Notes" },
  copilot: { source: "copilot", label: "Copilot Notes" },
  forms: { source: "forms", label: "Microsoft Forms" },
  teams: { source: "teams", label: "Teams Chat" },
};

export function normalizeSource(raw: string): { source: EvidenceSource; label: string } {
  const key = (raw || "").trim().toLowerCase();
  return SOURCE_MAP[key] ?? { source: "csv", label: "CSV Import" };
}

function toNum(v: string | undefined): number | null {
  if (v === undefined) return null;
  const n = parseFloat(v.trim());
  return Number.isFinite(n) ? n : null;
}

// Normalize parsed rows into participants. Works against the canonical
// ai-readiness schema; tolerant of missing optional columns.
export function normalizeRows(rows: string[][]): ParsedParticipant[] {
  if (rows.length < 2) return [];
  const headers = rows[0].map((h) => h.trim());
  const idx = (name: string) => headers.findIndex((h) => h.toLowerCase() === name.toLowerCase());

  const iId = idx("participantId");
  const iName = idx("name");
  const iDept = idx("department");
  const iRole = idx("role");
  const iTs = idx("timestamp");
  const vCols = [1, 2, 3, 4, 5].map((n) => idx(`v${n}_score`));
  const iSource = idx("source");
  const iNotes = idx("notes");

  const out: ParsedParticipant[] = [];
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r];
    const get = (i: number) => (i >= 0 ? (cells[i] ?? "").trim() : "");
    const participantId = get(iId) || `row-${r}`;
    const { source, label } = normalizeSource(get(iSource));
    const vectorScores = vCols.map((ci) => (ci >= 0 ? toNum(cells[ci]) : null));
    out.push({
      participantId,
      name: get(iName) || participantId,
      department: get(iDept),
      role: get(iRole),
      timestamp: get(iTs),
      vectorScores,
      source,
      sourceLabel: label,
      notes: get(iNotes) || null,
    });
  }
  return out;
}

// ── Graded-quote ingestion (Copilot / Lucidchart) ───────────────────────────
// These tables are the output of the AI-readiness quote analysis: one row per
// quote, each tagged with a Dimension (1..5) and a Score (1..5). The Dimension
// number maps directly onto the five readiness vectors.

export interface ParsedQuote {
  rowNum: number | null;
  speaker: string;
  role: string;
  quote: string;
  day: string;
  session: string;
  segment: string;
  vectorIndex: number; // 1..5, from the Dimension column
  dimensionLabel: string;
  score: number | null; // 1..5
  why: string;
}

// Canonical short labels for each readiness vector / dimension.
export const DIMENSION_LABELS: Record<number, string> = {
  1: "AI Role Clarity",
  2: "Tools & Workflow",
  3: "Org Alignment",
  4: "Skills & Capability",
  5: "Transformational Value",
};

// A Dimension cell looks like "5 — Transformational Value" but the separator is
// frequently mojibake ("5 ó Transformational Value"). Pull the leading 1..5 and
// strip any non-alphanumeric lead-in from the remaining label.
export function parseDimension(cell: string): { index: number | null; label: string } {
  const raw = cell || "";
  const m = raw.match(/[1-5]/);
  const index = m ? parseInt(m[0], 10) : null;
  const label = raw
    .replace(/^.*?[1-5]/, "") // drop everything up to and including the digit
    .replace(/^[^A-Za-z0-9]+/, "") // drop the separator (incl. "ó"/"—"/"→")
    .trim();
  return { index, label };
}

export function normalizeQuotes(rows: string[][]): ParsedQuote[] {
  if (rows.length < 2) return [];
  const lower = rows[0].map((h) => h.trim().toLowerCase());
  const find = (pred: (h: string) => boolean) => lower.findIndex(pred);

  const iNum = find((h) => h === "#" || h === "no" || h === "row" || h === "id");
  const iSpeaker = find((h) => h.includes("speaker") || h === "name" || h === "participant");
  const iRole = find((h) => h.includes("role") || h.includes("title"));
  const iQuote = find((h) => h.includes("quote"));
  const iDay = find((h) => h.includes("day"));
  const iSession = find((h) => h.includes("session"));
  const iSegment = find((h) => h.includes("segment"));
  const iDim = find((h) => h.includes("dimension") || h.includes("vector"));
  // "Why this score" also contains "score" — prefer an exact/non-why match.
  const iScore = find((h) => h === "score" || (h.includes("score") && !h.includes("why")));
  const iWhy = find((h) => h.includes("why") || h.includes("rationale") || h.includes("reason"));

  const out: ParsedQuote[] = [];
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r];
    const get = (i: number) => (i >= 0 ? (cells[i] ?? "").trim() : "");
    const quote = get(iQuote);
    const speaker = get(iSpeaker);
    if (!quote && !speaker) continue; // skip blank rows
    const { index, label } = parseDimension(get(iDim));
    if (index === null) continue; // a quote with no dimension can't be scored
    out.push({
      rowNum: toNum(get(iNum)),
      speaker: speaker || "Unknown",
      role: get(iRole),
      quote,
      day: get(iDay),
      session: get(iSession),
      segment: get(iSegment),
      vectorIndex: index,
      dimensionLabel: label || DIMENSION_LABELS[index],
      score: toNum(get(iScore)),
      why: get(iWhy),
    });
  }
  return out;
}

// Map a dimension/theme cell to a vector index. Accepts an explicit 1..5, or
// matches the theme by keyword (e.g. a Lucidchart board frame named after a
// readiness area).
export function matchDimensionLabel(label: string): number | null {
  const raw = label || "";
  const n = raw.match(/[1-5]/);
  if (n) return parseInt(n[0], 10);
  const t = raw.toLowerCase();
  if (/\brole\b|defin|clarit|understood|purpose/.test(t)) return 1;
  if (/tool|workflow|platform|technolog|software/.test(t)) return 2;
  if (/org|process|structure|align|governance|operat/.test(t)) return 3;
  if (/skill|capab|talent|train|upskill|learn/.test(t)) return 4;
  if (/transform|value|roi|outcome|impact|growth/.test(t)) return 5;
  return null;
}

// ── Lucidchart sticky-note ingestion ─────────────────────────────────────────
// A board export: one sticky per row. Free-form text grouped under a theme that
// maps to a readiness vector. Stickies are qualitative — a score is optional.

export interface ParsedSticky {
  author: string;
  text: string;
  vectorIndex: number; // 1..5
  dimensionLabel: string;
  score: number | null;
  day: string;
  session: string;
  segment: string;
}

export function normalizeStickies(rows: string[][]): ParsedSticky[] {
  if (rows.length < 2) return [];
  const lower = rows[0].map((h) => h.trim().toLowerCase());
  const find = (pred: (h: string) => boolean) => lower.findIndex(pred);

  const iText = find(
    (h) =>
      h.includes("sticky") ||
      h.includes("note") ||
      h.includes("content") ||
      h.includes("text") ||
      h.includes("comment") ||
      h.includes("quote")
  );
  const iAuthor = find(
    (h) =>
      h.includes("author") ||
      h.includes("speaker") ||
      h.includes("participant") ||
      h.includes("created by") ||
      h.includes("owner") ||
      h === "name"
  );
  const iDim = find(
    (h) =>
      h.includes("dimension") ||
      h.includes("vector") ||
      h.includes("theme") ||
      h.includes("category") ||
      h.includes("frame") ||
      h.includes("board") ||
      h.includes("topic")
  );
  const iScore = find((h) => h === "score" || (h.includes("score") && !h.includes("why")) || h.includes("rating"));
  const iDay = find((h) => h.includes("day"));
  const iSession = find((h) => h.includes("session"));
  const iSegment = find((h) => h.includes("segment"));

  const out: ParsedSticky[] = [];
  for (let r = 1; r < rows.length; r++) {
    const cells = rows[r];
    const get = (i: number) => (i >= 0 ? (cells[i] ?? "").trim() : "");
    const text = get(iText);
    if (!text) continue;
    const dimRaw = get(iDim);
    const index = matchDimensionLabel(dimRaw);
    if (index === null) continue; // can't place a sticky with no theme/dimension
    out.push({
      author: get(iAuthor) || "Participant",
      text,
      vectorIndex: index,
      dimensionLabel: DIMENSION_LABELS[index],
      score: toNum(get(iScore)),
      day: get(iDay),
      session: get(iSession),
      segment: get(iSegment),
    });
  }
  return out;
}

// Lightweight lexical sentiment used by the ingestion preview (ported from demo).
const POSITIVE = [
  "strong", "growth", "ready", "transform", "enabler", "leadership", "engaged",
  "scale", "confident", "clear", "good", "native", "invest",
];
const NEGATIVE = [
  "gap", "gaps", "weak", "risk", "risks", "cautious", "immature", "needed",
  "limited", "early", "lacking", "concern", "slow",
];

export function scoreSentiment(text: string | null | undefined): number {
  if (!text) return 0;
  const t = text.toLowerCase();
  let score = 0;
  for (const w of POSITIVE) if (t.includes(w)) score += 1;
  for (const w of NEGATIVE) if (t.includes(w)) score -= 1;
  return score;
}
